import { checkinsApi, type CheckinRequest } from './api';

interface QueuedCheckin {
  id: string;
  eventId: string;
  data: CheckinRequest;
  timestamp: number;
}

const STORAGE_KEY = 'oro_checkin_queue';

// Get queued check-ins from localStorage
export function getQueuedCheckins(): QueuedCheckin[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to load queued check-ins:', error);
    return [];
  }
}

// Save queued check-ins to localStorage
function saveQueuedCheckins(queue: QueuedCheckin[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error('Failed to save queued check-ins:', error);
  }
}

// Add a check-in to the queue
export function queueCheckin(eventId: string, data: CheckinRequest): string {
  const queue = getQueuedCheckins();
  const queuedCheckin: QueuedCheckin = {
    id: crypto.randomUUID(),
    eventId,
    data: { ...data, source: 'offline' }, // Force offline source
    timestamp: Date.now(),
  };
  
  queue.push(queuedCheckin);
  saveQueuedCheckins(queue);
  
  return queuedCheckin.id;
}

// Remove a check-in from the queue
function removeFromQueue(id: string): void {
  const queue = getQueuedCheckins();
  const filtered = queue.filter((item) => item.id !== id);
  saveQueuedCheckins(filtered);
}

// Sync all queued check-ins with the server
export async function syncQueuedCheckins(): Promise<{
  success: number;
  failed: number;
  errors: Array<{ eventId: string; error: string }>;
}> {
  const queue = getQueuedCheckins();
  
  if (queue.length === 0) {
    return { success: 0, failed: 0, errors: [] };
  }

  let success = 0;
  let failed = 0;
  const errors: Array<{ eventId: string; error: string }> = [];

  for (const item of queue) {
    try {
      await checkinsApi.createCheckin(item.eventId, item.data);
      removeFromQueue(item.id);
      success++;
    } catch (error) {
      failed++;
      errors.push({
        eventId: item.eventId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  return { success, failed, errors };
}

// Check if browser is online
export function isOnline(): boolean {
  return navigator.onLine;
}

// Setup online/offline event listeners
export function setupOfflineSync(onSync?: (result: {
  success: number;
  failed: number;
  errors: Array<{ eventId: string; error: string }>;
}) => void): () => void {
  const handleOnline = async () => {
    console.log('Browser is online, syncing queued check-ins...');
    const result = await syncQueuedCheckins();
    
    if (result.success > 0 || result.failed > 0) {
      console.log(`Sync complete: ${result.success} success, ${result.failed} failed`);
      if (onSync) {
        onSync(result);
      }
    }
  };

  window.addEventListener('online', handleOnline);

  // Cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
  };
}

// Get count of queued check-ins
export function getQueuedCount(): number {
  return getQueuedCheckins().length;
}
